<template>
  <div class="d-flex flex-column ga-4">
    <v-alert v-if="error" type="error" variant="tonal">{{ error }}</v-alert>
    <v-alert v-if="session?.isComplete.value" type="success" variant="tonal">
      Your order is complete.
      <v-btn variant="text" @click="props.nextStep">Continue</v-btn>
    </v-alert>
    <template v-else-if="!session?.ready.value">
      <v-progress-linear v-if="session?.busy.value" indeterminate />
      <v-alert v-else type="warning" variant="tonal">
        Your cart must be loaded before checkout.
        <v-btn
          v-if="session"
          variant="text"
          :disabled="working"
          @click="refresh"
          >Refresh payment status</v-btn
        >
      </v-alert>
    </template>
    <template v-else-if="session.order.value?.orderStatus === OrderStatus.OPEN">
      <v-alert
        v-if="blockingProblems.length"
        type="warning"
        variant="tonal"
        title="Before you can pay"
      >
        <ul class="ml-4">
          <li
            v-for="problem in blockingProblems"
            :key="`${problem.stage}:${problem.message}`"
          >
            {{ problem.message }}
          </li>
        </ul>
      </v-alert>
      <div class="d-flex justify-space-between">
        <span>Order total</span>
        <strong>{{ money(session.order.value.amountTotalCents) }}</strong>
      </div>
      <v-btn
        color="primary"
        :loading="working"
        :disabled="
          !session.order.value.lineItems?.length || !session.canPay.value
        "
        @click="beginCheckout"
        >Continue to Payment</v-btn
      >
    </template>
    <template
      v-else-if="session.order.value?.orderStatus === OrderStatus.PAYING"
    >
      <div class="d-flex justify-space-between">
        <span>Amount remaining</span>
        <strong>{{ money(remainingCents) }}</strong>
      </div>
      <div
        v-if="session.order.value.amountTotalCents !== 0 && remainingCents > 0"
        data-testid="gift-card-payment"
      >
        <h4 class="mb-2">Apply Gift Card</h4>
        <div class="d-flex align-start ga-2">
          <v-text-field
            v-model="balanceNumber"
            label="Gift card or house account number"
            :error-messages="balanceError"
            :disabled="working"
            @keyup.enter="findBalance"
          />
          <v-btn :disabled="working || !balanceNumber" @click="findBalance"
            >Search</v-btn
          >
        </div>
        <v-alert v-if="balance" type="info" variant="tonal" class="mb-2">
          Available balance: {{ money(balance.spendableAmountCents) }}
          <div>
            {{ money(balanceAmountCents) }} will be applied.
            <template v-if="balanceAmountCents < remainingCents">
              The remaining {{ money(remainingCents - balanceAmountCents) }}
              can be paid by card.
            </template>
          </div>
          <v-btn
            class="mt-2"
            color="primary"
            :loading="working"
            :disabled="working || balanceAmountCents <= 0"
            @click="applyBalance"
            >Apply balance</v-btn
          >
        </v-alert>
        <v-divider class="mt-2" />
      </div>
      <OrderPaymentDetails
        v-if="settledAttempts.length"
        :payments="settledAttempts"
        member
      >
        <template #actions="{ payment: attempt }">
          <v-btn
            v-if="
              'payment' in attempt &&
              attempt.status === PaymentStatus.CAPTURABLE
            "
            variant="text"
            color="error"
            :disabled="working"
            @click="cancelPayment(attempt.payment)"
            >Cancel payment</v-btn
          >
        </template>
      </OrderPaymentDetails>
      <div
        v-for="attempt in pendingCards"
        :key="attempt.payment.assignId"
        data-testid="card-payment"
      >
        <StaffOrderCardPayment
          v-if="attempt.clientSecret"
          :key="attempt.clientSecret"
          :client-secret="attempt.clientSecret"
          :customer-session="() => customerSession(attempt.payment)"
          :disabled="working"
          @confirmed="completePayment"
        />
        <v-btn
          v-else
          variant="text"
          :disabled="working"
          @click="preparePayment(attempt.payment)"
          >Retry card preparation</v-btn
        >
      </div>
      <v-divider />
      <v-btn
        v-if="session.order.value.amountTotalCents === 0"
        color="primary"
        :loading="working"
        :disabled="working"
        @click="completePayment"
        >Complete free order</v-btn
      >
      <template v-else-if="remainingCents > 0">
        <v-btn variant="text" :disabled="working" @click="refresh"
          >Refresh payment status</v-btn
        >
        <v-btn
          v-if="!pendingCards.length && autoFailed"
          color="primary"
          :loading="working"
          :disabled="working"
          @click="addCardPayment"
          >Pay by card</v-btn
        >
      </template>
    </template>
    <v-alert v-else type="info" variant="tonal">
      Order status: {{ session.order.value?.orderStatus ?? "Unknown" }}
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { useBalanceService } from "../../../cbi-generator/frontend/composables/useBalanceFrontendService";
import type { BalanceMember } from "../../../cbi-generator/generated/dto/BalanceMember";
import { CheckoutStage } from "../../../cbi-generator/generated/dto/CheckoutStage";
import type { OrderPaymentRef } from "../../../cbi-generator/generated/dto/OrderPaymentRef";
import { OrderStatus } from "../../../cbi-generator/generated/dto/OrderStatus";
import { PaymentStatus } from "../../../cbi-generator/generated/dto/PaymentStatus";
import { PaymentType } from "../../../cbi-generator/generated/dto/PaymentType";
import type { StartPayment } from "../../../cbi-generator/generated/dto/StartPayment";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import { useNotificationStore } from "@/stores/notification";
import { usePaymentService } from "../../../cbi-generator/frontend/composables/usePaymentFrontendService";
import StaffOrderCardPayment from "./StaffOrderCardPayment.vue";
import OrderPaymentDetails from "./OrderPaymentDetails.vue";
import { memberPaymentVisible } from "@/composables/shop/paymentStatus";

const props = defineProps<{
  nextStep: () => void;
  previousStep: () => void;
  /** Whether the payment step is open; checkout and card setup start automatically while it is. */
  active?: boolean;
}>();
const session = inject<MemberOrderSession | null>(memberOrderSessionKey, null);
// Purchaser and agreement problems are shown, and gate, their own checkout steps.
const blockingProblems = computed(
  () =>
    session?.readiness.value?.problems.filter(
      (problem) =>
        problem.stage !== CheckoutStage.PURCHASER &&
        problem.stage !== CheckoutStage.AGREEMENTS,
    ) ?? [],
);
const notification = useNotificationStore();
const paymentService = usePaymentService();
const customerSession = async (payment: OrderPaymentRef) =>
  (await paymentService.paymentCustomerSessionMember(payment)).clientSecret;

const balanceNumber = ref("");
const balance = shallowRef<BalanceMember | null>(null);
const balanceError = ref("");
const working = ref(false);
const error = ref("");

const money = (amount: number | null | undefined) =>
  amount == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount / 100);

async function act(action: () => Promise<unknown>, automatic = false) {
  if (!session || working.value) return;
  working.value = true;
  error.value = "";
  try {
    await action();
    if (session.isComplete.value) props.nextStep();
  } catch (cause) {
    if (automatic) autoFailed.value = true;
    error.value =
      cause instanceof Error ? cause.message : "Unable to process payment.";
    notification.addSnackbar(error.value, "error");
  } finally {
    working.value = false;
  }
}

function beginCheckout() {
  if (!session) return;
  autoFailed.value = false;
  void act(() => session.beginCheckout());
}

async function findBalance() {
  balanceError.value = "";
  balance.value = null;
  if (!balanceNumber.value.trim()) {
    balanceError.value = "Enter an account number.";
    return;
  }
  try {
    balance.value = await useBalanceService().findBalanceByNumMember(
      balanceNumber.value.trim(),
    );
  } catch (cause) {
    balanceError.value =
      cause instanceof Error ? cause.message : "Balance account not found.";
  }
}

// Card payments stay PENDING until confirmed (their card form is shown separately); balances are
// allocated as CAPTURABLE immediately. Members only see payments that went through.
const settledAttempts = computed(
  () =>
    session?.attempts.value.filter((attempt) =>
      memberPaymentVisible(attempt.status),
    ) ?? [],
);
const pendingCards = computed(
  () =>
    session?.attempts.value.filter(
      (attempt) => attempt.status === PaymentStatus.PENDING,
    ) ?? [],
);
/** Amount still owed once pending card placeholders are released. */
const remainingCents = computed(
  () =>
    (session?.availableCents.value ?? 0) +
    pendingCards.value.reduce((sum, attempt) => sum + attempt.amountCents, 0),
);
const balanceAmountCents = computed(() =>
  Math.max(
    0,
    Math.min(remainingCents.value, balance.value?.spendableAmountCents ?? 0),
  ),
);

function request(type: PaymentType, amountCents: number): StartPayment {
  return {
    type,
    amountCents,
    balance:
      type === PaymentType.BALANCE && balance.value
        ? { assignId: balance.value.assignId }
        : null,
    cashDrawer: null,
    tenderedCents: null,
    reference: null,
    memo: null,
    otherType: null,
  };
}

async function startCard() {
  if (!session) return;
  const amountCents = session.availableCents.value;
  if (amountCents == null || amountCents <= 0) return;
  const payment = await session.allocate(
    request(PaymentType.STRIPE, amountCents),
  );
  autoPrepared.add(payment.assignId);
  await session.prepare(payment);
}

function addCardPayment() {
  autoFailed.value = false;
  void act(startCard);
}

async function applyBalance() {
  if (!session || !balance.value) return;
  await act(async () => {
    for (const attempt of pendingCards.value)
      await session.cancelPayment(attempt.payment);
    const amountCents = Math.min(
      session.availableCents.value ?? 0,
      balance.value?.spendableAmountCents ?? 0,
    );
    if (!Number.isSafeInteger(amountCents) || amountCents <= 0)
      throw new Error("This balance has no available funds.");
    const payment = await session.allocate(
      request(PaymentType.BALANCE, amountCents),
    );
    balance.value = null;
    balanceNumber.value = "";
    if (session.availableCents.value === 0) await session.complete();
  });
}

const autoFailed = ref(false);
// Each card is prepared automatically at most once; later retries are manual.
const autoPrepared = new Set<number>();
async function advance() {
  if (
    !session ||
    !props.active ||
    autoFailed.value ||
    working.value ||
    session.busy.value ||
    !session.ready.value
  )
    return;
  const order = session.order.value;
  if (
    order?.orderStatus === OrderStatus.OPEN &&
    order.lineItems?.length &&
    session.canPay.value
  ) {
    await act(() => session.beginCheckout(), true);
  } else if (
    order?.orderStatus === OrderStatus.PAYING &&
    order.amountTotalCents !== 0
  ) {
    const available = session.availableCents.value ?? 0;
    const unprepared = pendingCards.value.find(
      (attempt) =>
        !attempt.clientSecret && !autoPrepared.has(attempt.payment.assignId),
    );
    if (available > 0 && pendingCards.value.length)
      // The amount owed changed (e.g. a balance was removed); resize the card.
      await act(async () => {
        for (const attempt of pendingCards.value)
          await session.cancelPayment(attempt.payment);
        await startCard();
      }, true);
    else if (available > 0) await act(startCard, true);
    else if (unprepared) {
      autoPrepared.add(unprepared.payment.assignId);
      await act(() => session.prepare(unprepared.payment), true);
    }
  }
}
watch(
  () => [
    props.active,
    session?.ready.value,
    session?.busy.value,
    session?.order.value?.orderStatus,
    session?.availableCents.value,
    pendingCards.value.length,
    working.value,
  ],
  () => void advance(),
  { immediate: true },
);
function preparePayment(payment: OrderPaymentRef) {
  if (!session) return;
  void act(() => session.prepare(payment));
}

function completePayment() {
  if (!session) return;
  void act(() => session.complete());
}

function cancelPayment(payment: OrderPaymentRef) {
  if (!session) return;
  void act(() => session.cancelPayment(payment));
}

function refresh() {
  if (!session) return;
  autoFailed.value = false;
  void act(() => session.refresh());
}
</script>
